import { CheckCircle2, CircleAlert, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * Notifications de fin d'action (cahier des charges §4) : un message pour
 * chaque succès ou échec, lu par les lecteurs d'écran, qui disparaît seul.
 */
interface Notification {
  id: number;
  ton: 'succes' | 'erreur';
  message: string;
}

interface Notifier {
  succes: (message: string) => void;
  erreur: (message: string) => void;
}

const Contexte = createContext<Notifier | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [liste, setListe] = useState<Notification[]>([]);
  const suivant = useRef(1);

  const retirer = useCallback((id: number) => setListe((l) => l.filter((n) => n.id !== id)), []);

  const ajouter = useCallback(
    (ton: Notification['ton'], message: string) => {
      const id = suivant.current++;
      setListe((l) => [...l.slice(-3), { id, ton, message }]);
      window.setTimeout(() => retirer(id), ton === 'erreur' ? 8000 : 5000);
    },
    [retirer]
  );

  const notifier = useMemo<Notifier>(
    () => ({ succes: (m) => ajouter('succes', m), erreur: (m) => ajouter('erreur', m) }),
    [ajouter]
  );

  return (
    <Contexte.Provider value={notifier}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {liste.map((n) => (
          <div key={n.id} className={`toast toast--${n.ton}`}>
            {n.ton === 'succes' ? <CheckCircle2 size={16} aria-hidden="true" /> : <CircleAlert size={16} aria-hidden="true" />}
            <span className="toast__message">{n.message}</span>
            <button type="button" className="toast__close" onClick={() => retirer(n.id)} aria-label="Fermer la notification">
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </Contexte.Provider>
  );
}

export function useToast(): Notifier {
  const n = useContext(Contexte);
  if (!n) throw new Error('useToast doit être utilisé sous <ToastProvider>');
  return n;
}
